import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CalendarDays, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import useGeoCurrency, { priceLabel } from "@/lib/useGeoCurrency";
import { optimizeImageUrl } from "@/lib/imageUtils";
import SEOHead from "@/components/SEOHead";
import ServiceForm from "@/components/ServiceForm";
import FAQSection from "@/components/FAQSection";

export default function PackageDetail() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = useGeoCurrency();

  useEffect(() => {
    api.get(`/packages/${slug}`).then((res) => setPkg(res.data)).catch(() => setPkg(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[var(--brand-primary)]" size={32} /></div>;
  if (!pkg) return <div className="min-h-[60vh] flex items-center justify-center text-[var(--brand-secondary)]">Package not found.</div>;

  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": pkg.title,
    "description": pkg.short_description,
    "image": pkg.banner_image || undefined,
    "provider": { "@type": "TravelAgency", "name": "Gulfudaan" },
  };
  const schemas = [schema];
  if (pkg.faqs?.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": pkg.faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
  }

  return (
    <div data-testid="package-detail-page" className="bg-white pb-16 sm:pb-24">
      <SEOHead pageKey={`package-${slug}`} schema={schemas} />
      <div className="relative h-[30vh] sm:h-[45vh] min-h-[220px]">
        <img src={optimizeImageUrl(pkg.banner_image, 1400)} alt={pkg.title} fetchPriority="high" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A1A1A]/50 flex flex-col items-center justify-center text-center px-4">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)]">{pkg.package_type}</span>
          <h1 data-testid="package-detail-title" className="font-heading text-3xl sm:text-4xl text-white mt-2">{pkg.title}</h1>
          <span className="text-white text-sm flex items-center gap-1.5 mt-3"><CalendarDays size={14} /> {pkg.duration_days} Days</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {pkg.gallery?.length > 0 && (
            <div data-testid="package-gallery" className="grid grid-cols-3 gap-2">
              {pkg.gallery.map((g, i) => (
                <img key={i} src={optimizeImageUrl(g, 400)} alt={`${pkg.title} ${i + 1}`} loading="lazy" className="w-full h-28 sm:h-36 object-cover rounded-md" />
              ))}
            </div>
          )}

          <div>
            <h2 className="font-heading text-2xl text-[#1A1A1A] mb-3">Overview</h2>
            <p data-testid="package-long-description" className="text-[var(--brand-secondary)] leading-relaxed whitespace-pre-line">{pkg.long_description || pkg.short_description}</p>
          </div>

          {pkg.itinerary?.length > 0 && (
            <div data-testid="package-itinerary">
              <h2 className="font-heading text-2xl text-[#1A1A1A] mb-4">Itinerary</h2>
              <div className="space-y-4">
                {pkg.itinerary.map((it, i) => (
                  <div key={i} className="flex gap-4 border-l-2 border-[var(--brand-primary)] pl-4 py-1">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-[var(--brand-primary)]">{it.day}</p>
                      <p className="font-medium text-[#1A1A1A]">{it.title}</p>
                      <p className="text-sm text-[var(--brand-secondary)] mt-1">{it.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {pkg.inclusions?.length > 0 && (
              <div data-testid="package-inclusions">
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Inclusions</h3>
                <ul className="space-y-2">
                  {pkg.inclusions.map((inc, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[var(--brand-secondary)]"><CheckCircle2 size={15} className="text-green-600 shrink-0 mt-0.5" /> {inc}</li>
                  ))}
                </ul>
              </div>
            )}
            {pkg.exclusions?.length > 0 && (
              <div data-testid="package-exclusions">
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Exclusions</h3>
                <ul className="space-y-2">
                  {pkg.exclusions.map((exc, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[var(--brand-secondary)]"><XCircle size={15} className="text-red-500 shrink-0 mt-0.5" /> {exc}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="p-4 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB] mb-4">
              <p className="text-sm text-[var(--brand-secondary)]">Starting from</p>
              <p data-testid="package-price" className="font-price text-3xl text-[#1A1A1A]">{priceLabel(pkg.price_inr, pkg.price_usd, currency)}<span className="text-sm font-normal text-[var(--brand-secondary)]"> / person</span></p>
            </div>
            <ServiceForm service={pkg} entityType="package" />
          </div>
        </div>
      </div>

      <FAQSection faqs={pkg.faqs} title={`FAQs about ${pkg.title}`} />
    </div>
  );
}
